import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import type { CreativeTemplate, CreativeTemplateWritePayload } from '../types/creative-template.types';
import { CreativeTemplateForm } from './creative-template-form';
import type { EntitySheetMode } from './vertical-profile-sheet';

interface CreativeTemplateSheetProps {
  open: boolean;
  mode: EntitySheetMode;
  template: CreativeTemplate | null;
  isSaving?: boolean;
  submitError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CreativeTemplateWritePayload) => void | Promise<void>;
}

export function CreativeTemplateSheet({
  open,
  mode,
  template,
  isSaving = false,
  submitError = null,
  onOpenChange,
  onSubmit,
}: CreativeTemplateSheetProps) {
  const isCreate = mode === 'create';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:max-w-3xl">
        <div className="border-b border-gray-400 px-6 py-5">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="text-xl font-medium text-black">
              {isCreate ? 'New creative template' : template?.payload.name ?? 'Creative template'}
            </SheetTitle>
            <SheetDescription className="text-sm text-gray-500">
              Edit role specs and breakpoint zones. Each save creates a new version.
            </SheetDescription>
          </SheetHeader>
          {!isCreate && template ? (
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs font-medium text-gray-500">Template ID</dt>
                <dd className="mt-0.5 text-black">{template.template_id}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Version</dt>
                <dd className="mt-0.5 text-black">{template.version ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Updated</dt>
                <dd className="mt-0.5 text-black">{formatDisplayDateTime(template.updated_at)}</dd>
              </div>
            </dl>
          ) : null}
        </div>

        <div className="flex-1 px-6 py-5">
          <CreativeTemplateForm
            key={template?.id ?? 'create'}
            initialTemplate={isCreate ? null : template}
            isCreate={isCreate}
            isSaving={isSaving}
            submitError={submitError}
            onSubmit={onSubmit}
            onCancel={() => onOpenChange(false)}
          />
        </div>

        {isSaving ? (
          <div className="sticky bottom-0 flex items-center gap-2 border-t border-gray-400 bg-black px-6 py-3 text-sm text-white" role="status">
            <Loader2 className="size-4 animate-spin" />
            Saving…
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
